import React, { ForwardedRef, forwardRef, useEffect, useState } from 'react';
import PhotoSwipe from 'photoswipe';
import PhotoSwipeUI_Default from 'photoswipe/dist/photoswipe-ui-default';
import { Img } from '../Img/Img';
import { chevron_left, chevron_right, Icon, close as close_icon } from '../../components/Icon/Icon';

import 'photoswipe/dist/photoswipe.css';
import './ImageGalleryPhotoswipe.css';
import './ImageGallery.css';
import { useTranslation } from '../../i18n/i18n';

type Image = {
	url: string;
	width: number;
	height: number;
};
type ImageGalleryProps = {
	images: Image[];
};

export const ImageGallery = forwardRef(({ images }: ImageGalleryProps, ref: ForwardedRef<HTMLImageElement>) => {
	const [index, setIndex] = useState(0);
	const [galleryRef, setGalleryRef] = useState<PhotoSwipe<PhotoSwipe.Options>>();
	const [isOpen, setIsOpen] = useState(false);
	const t = useTranslation().imageGallery;
	function getGallery({ index }: { index?: number }) {
		const pswpElement = document.querySelectorAll('.pswp')[0];
		const options: PhotoSwipe.Options & PhotoSwipeUI_Default.Options = {
			index: index || 0,
			history: false,
			shareEl: false,
			fullscreenEl: false,
			closeEl: false,
			arrowEl: false,
		};

		const items: PhotoSwipe.Item[] = images.map((image) => {
			return {
				src: image.url,
				w: image.width,
				h: image.height,
			};
		});

		const gallery = new PhotoSwipe(pswpElement as HTMLElement, PhotoSwipeUI_Default, items, options);
		gallery.listen('close', function () {
			setGalleryRef(undefined);
			setIsOpen(false);
		});
		gallery.listen('afterChange', function () {
			setIndex(gallery.getCurrentIndex());
		});
		setIsOpen(true);
		setGalleryRef(gallery);
		return gallery;
	}

	const openGalleryAt = (index: number) => {
		const gallery = getGallery({ index });
		gallery.init();
	};
	function goto(i: number) {
		galleryRef?.goTo(i);
	}
	const close = () => galleryRef?.close();
	const next = () => galleryRef?.next();
	const prev = () => galleryRef?.prev();

	useEffect(() => {
		if (index >= images.length) {
			setIndex(Math.max(images.length - 1, 0));
		}
	}, [images.length, index]);

	if (images.length === 0)
		return (
			<div className="image-gallery">
				<img src="/images/no-image.svg" width="100" alt="ingen bild" ref={ref} />
			</div>
		);

	const currentIndex = index >= 0 && index < images.length ? index : 0;
	const currentImage = images[currentIndex];

	return (
		<div className="image-gallery">
			<img
				ref={ref as React.MutableRefObject<HTMLImageElement>}
				alt={t.productImage}
				src={currentImage.url}
				width={currentImage.width}
				height={currentImage.height}
				onClick={() => openGalleryAt(currentIndex)}
			/>

			<GalleryBrowser images={images} index={currentIndex} onClick={setIndex} />
			<PhotoSwipeMarkup />

			{isOpen && (
				<div className="custom-ui-container">
					<button className="custom-ui button secondary close-button" onClick={close}>
						<Icon size={40} shape={close_icon} />
					</button>
					<button className="custom-ui button secondary left-button" onClick={prev}>
						<Icon size={40} shape={chevron_left} />
					</button>
					<button className="custom-ui button secondary right-button" onClick={next}>
						<Icon size={40} shape={chevron_right} />
					</button>

					<div className="custom-ui custom-ui-footer">
						<GalleryBrowser images={images} index={currentIndex} onClick={goto} />
					</div>
				</div>
			)}
		</div>
	);
});

interface GalleryBrowserProps {
	images: Image[];
	index: number;
	onClick: (i: number) => void;
}

function GalleryBrowser({ images, index, onClick }: GalleryBrowserProps) {
	const t = useTranslation().imageGallery;
	return (
		<div className="gallery-footer">
			<div className="thumbnail-browser">
				{images.map((image, i) => {
					return (
						<div className={'thumb ' + (i === index ? 'selected' : '')} key={i}>
							<Img
								alt={t.extraProductImage}
								src={image.url}
								width={97}
								height={97}
								imgWidth={97}
								imgHeight={97}
								onClick={(e) => {
									e.stopPropagation();
									onClick(i);
								}}
								className={i === index ? 'selected' : ''}
							/>
						</div>
					);
				})}
			</div>
		</div>
	);
}

function PhotoSwipeMarkup() {
	const t = useTranslation().imageGallery;
	return (
		<div className="pswp" tabIndex={-1} role="dialog" aria-hidden="true">
			<div className="pswp__bg"></div>
			<div className="pswp__scroll-wrap">
				<div className="pswp__container">
					<div className="pswp__item"></div>
					<div className="pswp__item"></div>
					<div className="pswp__item"></div>
				</div>
				<div className="pswp__ui pswp__ui--hidden">
					<div className="pswp__top-bar">
						<div className="pswp__counter"></div>
						<button className="pswp__button pswp__button--close" title="Close (Esc)"></button>
						<button className="pswp__button pswp__button--share" title="Share"></button>
						<button className="pswp__button pswp__button--fs" title="Toggle fullscreen"></button>
						<button className="pswp__button pswp__button--zoom" title={t.zoomInOut}></button>
						<div className="pswp__preloader">
							<div className="pswp__preloader__icn">
								<div className="pswp__preloader__cut">
									<div className="pswp__preloader__donut"></div>
								</div>
							</div>
						</div>
					</div>
					<div className="pswp__share-modal pswp__share-modal--hidden pswp__single-tap">
						<div className="pswp__share-tooltip"></div>
					</div>
					<button className="pswp__button pswp__button--arrow--left" title="Previous (arrow left)"></button>
					<button className="pswp__button pswp__button--arrow--right" title="Next (arrow right)"></button>
					<div className="pswp__caption">
						<div className="pswp__caption__center"></div>
					</div>
				</div>
			</div>
		</div>
	);
}
